import { Proto } from "../../../pojo/Proto";
import { S25WsNode } from "../../../pojo/S25WsNode";
import { Workflow } from "../../workflow/service/Workflow";
import { DropDownItem } from "../../../pojo/DropDownItem";

export namespace Budget {
    import NumericalBoolean = Proto.NumericalBoolean;
    import ServiceStatus = Proto.ServiceStatus;
    import ISODateString = Proto.ISODateString;
    import NumericalString = Proto.NumericalString;
    import WorkflowStatus = Workflow.WorkflowStatus;
    import WorkflowSubmissionEntry = Workflow.WorkflowSubmissionEntry;
    import WorkflowState = Workflow.WorkflowState;

    export const BUDGET_ITEM_TYPE_ID = 12_000;
    export const BUDGET_PLAN_OBJECT_TYPE = Workflow.BUDGET_PLAN_OBJECT_TYPE;
    export const BUDGET_PLAN_ITEM_TYPE_ID = 12_005;

    // Dropdown item with date range for budget selection
    export type BudgetDropdownItem = DropDownItem & {
        startDate?: ISODateString;
        endDate?: ISODateString;
    };

    // Base budget item interface extending S25WsNode
    export interface BaseBudgetItem extends S25WsNode {
        budgetId: number;
        name: string;
        description?: string;
        amount: number;
        currency: string;
        createUser: string;
        createDate: ISODateString;
        lastModUser: string;
        lastModDate: ISODateString;
        comments?: string;
    }

    // Fiscal Year - for defining fiscal year periods
    export interface FiscalYear extends S25WsNode {
        fiscalYearId: number;
        name: string;
        description?: string;
        startDate: ISODateString;
        endDate: ISODateString;
        createUser?: string;
        createDt?: ISODateString;
        lastModUser?: string;
        lastModDate?: ISODateString;
    }

    // Organization Budget - represents the overall budget allocation for an organization
    export interface OrgBudget extends BaseBudgetItem {
        organizationId: number;
        organizationName: string;
        fiscalYearId: number;
        fiscalYearName?: string;
        startDate: ISODateString;
        endDate: ISODateString;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        remainingBalance: number;
        budgetPlans: BudgetPlan[];
        categories: BudgetCategory[];
    }

    // Budget Plan - represents a planned spending allocation
    export interface BudgetPlan extends BaseBudgetItem {
        budgetPlanId: number;
        planType: BudgetPlanType | string;
        startDate: ISODateString;
        endDate: ISODateString;
        allocatedAmount: number | { source: string; parsedValue: number };
        spentAmount: number | { source: string; parsedValue: number };
        committedAmount: number | { source: string; parsedValue: number };
        remainingAmount?: number;
        budgetLineItems: BudgetLineItem[];

        // Workflow fields (from Workflow.WorkflowItem)
        workflowProcessId?: number;
        currentWorkflowStateId?: number;
        currentWorkflowState?: WorkflowState; // only stateName and contacts/actions populated
        contId?: number;
        todoId?: number;
        workflowStatus?: WorkflowStatus | null; // null or PENDING = draft
        requestedChanges?: string;
        submissionDate?: ISODateString;
        submitted?: boolean;

        // Workflow submission data
        workflowSubmissionList?: WorkflowSubmissionEntry[];
        itemTypeId?: number;
        itemId?: number;

        // Computed/frontend fields
        active?: boolean;
    }

    // Budget Plan Types
    export enum BudgetPlanType {
        ANNUAL = "annual",
        SEMESTER = "semester",
        QUARTER = "quarter",
        EVENT_SPECIFIC = "event_specific",
        OPERATIONAL = "operational",
        SPECIAL_PROJECT = "special_project",
    }

    export const BudgetPlanTypes = [
        { itemId: "quarterly", itemName: "Quarterly" },
        { itemId: "semester", itemName: "Semester" },
        { itemId: "annual", itemName: "Annual" },
        { itemId: "event", itemName: "Event-Specific" },
        { itemId: "special_project", itemName: "Special Project" },
    ];

    // Budget Category - for categorizing budget items
    export interface BudgetCategory extends S25WsNode {
        categoryId: number;
        name: string;
        description?: string;
        color?: string;
        icon?: string;
        isActive: NumericalBoolean;
        sortOrder: number;
        parentCategoryId?: number;
        subCategories?: BudgetCategory[];
    }

    // Budget Line Item - detailed breakdown within a budget plan
    export interface BudgetLineItem extends BaseBudgetItem {
        budgetPlanId: number;
        lineItemId: number;
        categoryId: number;
        categoryName: string;
        plannedAmount: number;
        actualAmount: number;
        committedAmount: number;
        allocatedAmount?: number;
        spentAmount?: number;
        remainingAmount?: number;
        quantity?: number;
        unitCost?: number;
        vendor?: string;
        purchaseOrderNumber?: string;
        receiptNumber?: string;
    }

    // Budget Summary - for dashboard displays
    export interface BudgetSummary {
        orgBudgetId: number;
        organizationId: number;
        organizationName: string;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        remainingBalance: number;
        utilizationPercentage: number;
        budgetPlansCount: number;
        activePlansCount: number;
        fiscalYearId: number;
        fiscalYear: FiscalYear;
    }

    // Budget Report Data
    export interface BudgetReportData {
        summary: {
            totalAllocated: number;
            totalSpent: number;
            totalCommitted: number;
            remainingBalance: number;
            transactionCount: number;
        };
        categoryBreakdown: Array<{
            categoryId: number;
            categoryName: string;
            allocated: number;
            spent: number;
            committed: number;
            remaining: number;
        }>;
        monthlySpending: Array<{
            month: string;
            spent: number;
            committed: number;
        }>;
        topVendors: Array<{
            vendor: string;
            totalSpent: number;
            transactionCount: number;
        }>;
        events: Array<{
            eventId: number;
            eventName: string;
            allocated: number;
            spent: number;
            committed: number;
        }>;
    }

    // Budget Settings - configuration for budget management
    export interface BudgetSettings {
        organizationId: number;
        allowOverBudget: boolean;
        overBudgetLimit: number;
        defaultCategories: BudgetCategory[];
        fiscalYearStart: string; // MM-DD format
        currency: string;
        decimalPlaces: number;
        autoCloseInactivePlans: boolean;
        inactivePlanThreshold: number; // days
        notificationSettings: BudgetNotificationSettings;
    }

    // Budget Notification Settings
    export interface BudgetNotificationSettings {
        lowBalanceThreshold: number;
        overBudgetThreshold: number;
        monthlyReport: boolean;
        reportRecipients: string[];
    }

    // Data interfaces for API responses
    export interface OrgBudgetResponse {
        budget: OrgBudget;
        summary: BudgetSummary;
        settings: BudgetSettings;
    }

    export interface BudgetPlanResponse {
        budgetPlan: BudgetPlan;
        orgBudget: OrgBudget;
        categories: BudgetCategory[];
    }

    // ============================================================================
    // ADMIN-SPECIFIC INTERFACES
    // ============================================================================

    export interface BudgetRequest {
        requestId: number;
        organizationId: number;
        organizationName: string;
        requestedAmount: number;
        requestDate: ISODateString;
        status: "pending" | "approved" | "rejected";
        fiscalYear: string;
        description?: string;
        requestedBy: string;
        reviewedBy?: string;
        reviewedDate?: ISODateString;
        reviewComments?: string;
        currency?: string;
    }

    export interface AdminBudgetSummary {
        fiscalYear: string;
        totalOrganizations: number;
        totalBudgetsCreated: number;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        pendingRequests: number;
        utilizationPercentage: number;
        averageBudgetAmount: number;
    }

    export interface OrganizationBudgetStatus {
        organizationId: number;
        organizationName: string;
        organizationType?: string;
        hasBudget: boolean;
        budgetId?: number;
        allocatedAmount?: number;
        spentAmount?: number;
        committedAmount?: number;
        utilizationPercentage?: number;
        budgetStatus?: "active" | "warning" | "critical" | "expired" | "upcoming";
        lastModDate?: ISODateString;
    }

    export interface BulkBudgetTemplate {
        fiscalYear: string;
        defaultAmount: number;
        currency: string;
        startDate: ISODateString;
        endDate: ISODateString;
        descriptionTemplate: string;
        categories?: BudgetCategory[];
    }

    export function getUtilization(budget: OrgBudget): number {
        if (!budget.totalAllocated) return 0;
        return (((budget.totalSpent || 0) + (budget.totalCommitted || 0)) / budget.totalAllocated) * 100;
    }
}
